import { FC, ReactNode, useState } from "react";
import { Popover, SearchInput } from "../../design-system";
import styles from "./SearchPopover.module.less";

export interface SearchPopoverProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** What the field shows; clicking it opens the popup. */
    trigger: ReactNode;
    placeholder?: string;
    /** The popup's body, given what was typed in the search box. */
    children: (query: string) => ReactNode;
    /** An action row at the bottom, e.g. one that clears the selection. */
    footer?: { label: ReactNode; onClick: () => void };
}

/**
 * A picker's popup: clicking the trigger opens a popover right below it with
 * a search box on top and the caller's list under it. Escape or a click
 * outside closes it. Opening starts from an empty search.
 */
export const SearchPopover: FC<SearchPopoverProps> = ({
    open,
    onOpenChange,
    trigger,
    placeholder = "Search…",
    children,
    footer,
}) => {
    const [query, setQuery] = useState("");

    const handleOpenChange = (next: boolean) => {
        if (next) setQuery("");
        onOpenChange(next);
    };

    const content = (
        <div className={styles.popup}>
            <div className={styles.search}>
                <SearchInput
                    autoFocus
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") onOpenChange(false);
                    }}
                />
            </div>
            {children(query)}
            {footer && (
                <div className={styles.footer} onClick={footer.onClick}>
                    {footer.label}
                </div>
            )}
        </div>
    );

    return (
        <Popover
            trigger="click"
            placement="bottomLeft"
            showArrow={false}
            // antd keeps the arrow's ~15px gap even without the arrow; sit
            // 4px under the field like a select's dropdown does.
            overlayStyle={{ paddingTop: 4 }}
            open={open}
            onOpenChange={handleOpenChange}
            content={content}
            flush
            destroyTooltipOnHide
        >
            {/* A row around the field may navigate on click; the picker's clicks must not. */}
            <span className={styles.anchor} onClick={(e) => e.stopPropagation()}>
                {trigger}
            </span>
        </Popover>
    );
};
